import React, { useEffect, useState } from "react";
import { MessagesSquare, Send, CheckCircle2 } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { usePermissions } from "@/lib/permissions";
import { PageHero, Section, StatCard, Badge, Button, Input, Textarea, Field, DataTable, EmptyState } from "@/components/premium/UI";
import { Pattern } from "@/components/premium/Patterns";
import { toast } from "sonner";

const CATEGORIES = [
  { key: "confusion",     label: "Confusion / unclear UI" },
  { key: "extra_clicks",  label: "Extra clicks needed" },
  { key: "missing_info",  label: "Missing information" },
  { key: "slow",          label: "Slow workflow" },
  { key: "duplicate",     label: "Duplicate data entry" },
  { key: "navigation",    label: "Poor navigation" },
  { key: "validation",    label: "Missing validation" },
  { key: "idea",          label: "Improvement idea" },
  { key: "bug",           label: "Bug / error" },
];

export default function Feedback() {
  const { user } = useAuth();
  const { has } = usePermissions();
  const canTriage = has("admin:settings:manage");
  const [rows, setRows] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ category: "confusion", module: "general", severity: "low", location: window.location.pathname });
  const [showForm, setShowForm] = useState(false);

  async function load() {
    setLoading(true);
    try {
      const [r, s] = await Promise.all([api.get("/feedback"), api.get("/feedback/stats")]);
      setRows(r.data || []); setStats(s.data);
    } catch { setRows([]); }
    finally { setLoading(false); }
  }
  useEffect(() => { load(); }, []);

  async function submit(e) {
    e.preventDefault();
    try {
      await api.post("/feedback", form);
      toast.success("Feedback recorded — thank you!");
      setForm({ category: "confusion", module: "general", severity: "low", location: window.location.pathname });
      setShowForm(false); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  }

  async function updateStatus(id, patch) {
    try { await api.patch(`/feedback/${id}`, patch); toast.success("Updated"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  }

  return (
    <div className="space-y-6" data-testid="feedback-page">
      <PageHero
        eyebrow="RC3 Operational Pilot"
        title="Continuous improvement register"
        subtitle="Home360 is in Operational Pilot. Every enhancement from here on must come from actual daily use. Report anything that slows you down — every entry counts."
        moduleKind="dashboard"
        actions={<Button variant="primary" onClick={() => setShowForm(v => !v)} data-testid="feedback-add-btn"><Send className="w-4 h-4" /> Report an issue</Button>}
      />

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <StatCard label="Open" value={stats?.open ?? 0} icon={MessagesSquare} tone="accent" testid="fb-stat-open" />
        <StatCard label="Acknowledged" value={stats?.acknowledged ?? 0} icon={MessagesSquare} tone="info" testid="fb-stat-ack" />
        <StatCard label="In progress" value={stats?.in_progress ?? 0} icon={MessagesSquare} tone="primary" testid="fb-stat-progress" />
        <StatCard label="Resolved" value={stats?.resolved ?? 0} icon={CheckCircle2} tone="success" testid="fb-stat-resolved" />
        <StatCard label="High severity open" value={stats?.high_severity_open ?? 0} icon={MessagesSquare} tone={stats?.high_severity_open > 0 ? "danger" : "muted"} testid="fb-stat-high" />
      </div>

      {showForm && (
        <Section title="Report an operational issue" description={`As ${user?.name || user?.username} · ${user?.role}`}>
          <form onSubmit={submit} className="grid md:grid-cols-3 gap-3">
            <Field label="Category">
              <select value={form.category} onChange={e => setForm(f => ({ ...f, category: e.target.value }))} className="w-full rounded-xl border border-input bg-card px-3 py-2.5 text-sm" data-testid="feedback-form-category">
                {CATEGORIES.map(c => <option key={c.key} value={c.key}>{c.label}</option>)}
              </select>
            </Field>
            <Field label="Module">
              <select value={form.module} onChange={e => setForm(f => ({ ...f, module: e.target.value }))} className="w-full rounded-xl border border-input bg-card px-3 py-2.5 text-sm">
                {["general", "dashboard", "poultry", "health", "sales", "projects", "finance", "inventory", "hr", "marketing", "reports", "notifications", "settings"].map(m => <option key={m}>{m}</option>)}
              </select>
            </Field>
            <Field label="Severity">
              <select value={form.severity} onChange={e => setForm(f => ({ ...f, severity: e.target.value }))} className="w-full rounded-xl border border-input bg-card px-3 py-2.5 text-sm">
                {["low", "medium", "high"].map(v => <option key={v}>{v}</option>)}
              </select>
            </Field>
            <Field label="Summary" className="md:col-span-3">
              <Input value={form.summary || ""} onChange={e => setForm(f => ({ ...f, summary: e.target.value }))} required placeholder="Short description of the issue" data-testid="feedback-form-summary" />
            </Field>
            <Field label="Detail" className="md:col-span-3">
              <Textarea value={form.detail || ""} onChange={e => setForm(f => ({ ...f, detail: e.target.value }))} placeholder="Steps to reproduce, what you expected, screenshots reference, etc." />
            </Field>
            <Field label="Where (screen / URL)"><Input value={form.location} onChange={e => setForm(f => ({ ...f, location: e.target.value }))} /></Field>
            <Field label="Workaround (optional)" className="md:col-span-2"><Input value={form.workaround || ""} onChange={e => setForm(f => ({ ...f, workaround: e.target.value }))} /></Field>
            <div className="md:col-span-3 flex justify-end gap-2">
              <Button type="button" variant="ghost" onClick={() => setShowForm(false)}>Cancel</Button>
              <Button type="submit" variant="primary" data-testid="feedback-form-submit">Submit feedback</Button>
            </div>
          </form>
        </Section>
      )}

      <Section title="All feedback items" description="Filtered by nothing — every item is visible to promote transparency." className="relative overflow-hidden">
        <Pattern kind="dashboard" />
        <div className="relative">
          {loading ? <div className="animate-pulse space-y-2">{[1,2,3].map(i => <div key={i} className="h-10 bg-muted/40 rounded-lg" />)}</div>
          : rows.length === 0 ? <EmptyState icon={MessagesSquare} title="No feedback yet" description="Be the first to log an operational issue or improvement idea." />
          : (
            <DataTable
              testid="feedback-table"
              columns={[
                { header: "When", cell: r => r.created_at?.slice(0, 16).replace("T", " ") },
                { header: "Reporter", cell: r => <span className="font-medium">{r.reporter}</span> },
                { header: "Category", cell: r => <Badge variant="primary">{r.category}</Badge> },
                { header: "Module", cell: r => <Badge variant="neutral">{r.module}</Badge> },
                { header: "Summary", cell: r => <span className="line-clamp-2">{r.summary}</span> },
                { header: "Severity", cell: r => <Badge variant={r.severity === "high" ? "critical" : r.severity === "medium" ? "pending" : "neutral"}>{r.severity}</Badge> },
                { header: "Status", cell: r => <Badge variant={r.status === "resolved" ? "healthy" : r.status === "in_progress" ? "primary" : r.status === "wont_fix" ? "cancelled" : "pending"}>{r.status}</Badge> },
                ...(canTriage ? [{ header: "Triage", cell: r => (
                  <select value={r.status} onChange={e => updateStatus(r.id, { status: e.target.value })} className="rounded-lg border border-border bg-card px-2 py-1 text-xs" data-testid={`fb-triage-${r.id}`}>
                    {["open", "acknowledged", "in_progress", "resolved", "wont_fix"].map(s => <option key={s}>{s}</option>)}
                  </select>
                ) }] : []),
              ]}
              rows={rows}
            />
          )}
        </div>
      </Section>
    </div>
  );
}
